import { Link } from "react-router-dom";
import FeaturedPlacementBadge from "../FeaturedPlacementBadge";
import { trackSeoPageEvent, SEO_ANALYTICS_EVENTS } from "../../../constants/seoAnalyticsConstants";
import { buildSearchResultsHref } from "../../../services/seoPageService";
import { updateAttributionContext } from "../../../utils/campaignAttribution";
import {
  normalizeVehicleListings,
  shouldShowListingInSearch,
  toListingAnalyticsPayload,
} from "../../../utils/automapListing";
import VehicleSearchResultCard from "../search/VehicleSearchResultCard";
import {
  dedupeListingsById,
} from "../../../constants/automapFeaturedPlacementConstants";
import "../../../styles/featuredPlacement.css";

export default function SeoInventoryModule({
  config,
  listings = [],
  featuredListings = [],
  total = 0,
  loading = false,
  error = null,
}) {
  const searchHref = buildSearchResultsHref(config);
  const normalizedFeatured = normalizeVehicleListings(featuredListings);
  const normalizedListings = normalizeVehicleListings(listings).filter(shouldShowListingInSearch);
  const displayListings = normalizedFeatured.length
    ? [...normalizedFeatured, ...dedupeListingsById(normalizedFeatured, normalizedListings)]
    : normalizedListings;
  const featuredIds = new Set(normalizedFeatured.map((l) => l.id));

  if (loading) {
    return (
      <section className="seo-inventory" aria-busy="true">
        <p className="seo-inventory__status">Cargando inventario…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="seo-inventory">
        <p className="seo-inventory__status">No pudimos cargar el inventario. Intenta de nuevo más tarde.</p>
        <Link
          to={searchHref}
          className="seo-btn seo-btn--primary"
          onClick={() => trackSeoPageEvent(SEO_ANALYTICS_EVENTS.NO_INVENTORY_CTA_CLICKED, { source: "error" })}
        >
          Ver búsqueda completa
        </Link>
      </section>
    );
  }

  if (!displayListings.length) {
    return (
      <section className="seo-inventory seo-inventory--empty">
        <h2>Inventario disponible</h2>
        <p>
          Por ahora no hay publicaciones activas que coincidan con esta búsqueda. Explora opciones en la búsqueda
          general o en ciudades cercanas.
        </p>
        <div className="seo-inventory__empty-actions">
          <Link
            to={searchHref}
            className="seo-btn seo-btn--primary"
            onClick={() => trackSeoPageEvent(SEO_ANALYTICS_EVENTS.NO_INVENTORY_CTA_CLICKED, { source: "empty" })}
          >
            Ver búsqueda con filtros
          </Link>
          <Link
            to="/v/automap/comprar/quiz"
            className="seo-btn seo-btn--secondary"
            onClick={() => trackSeoPageEvent(SEO_ANALYTICS_EVENTS.BUYER_QUIZ_CLICKED, { source: "empty_inventory" })}
          >
            Ayúdame a escoger
          </Link>
        </div>
      </section>
    );
  }

  const countLabel =
    total <= 3
      ? `${total} opción${total === 1 ? "" : "es"} disponible${total === 1 ? "" : "s"}`
      : `${total} vehículos encontrados`;

  return (
    <section className="seo-inventory" aria-labelledby="seo-inventory-heading">
      <div className="seo-inventory__header">
        <h2 id="seo-inventory-heading">Inventario disponible</h2>
        <p>{countLabel}</p>
        <Link
          to={searchHref}
          className="seo-inventory__search-link"
          onClick={() => trackSeoPageEvent(SEO_ANALYTICS_EVENTS.FILTER_CLICKED, { source: "view_all" })}
        >
          Ver todos con filtros →
        </Link>
      </div>
      <div className="seo-inventory__grid">
        {displayListings.map((listing, index) => (
          <VehicleSearchResultCard
            key={listing.id}
            listing={listing}
            slug="automap"
            variant="compact"
            position={index}
            sourcePage={config.path}
            featuredMeta={
              featuredIds.has(listing.id)
                ? { placementId: listing.placementId, slotKey: listing.slotKey }
                : null
            }
            onCardClick={() => {
              const analytics = toListingAnalyticsPayload(listing, {
                sourcePage: config.path,
                sourceComponent: "seo_inventory_card",
                seoPageType: config.pageType,
                seoPageSlug: config.slug,
              });
              updateAttributionContext({
                sourceComponent: "seo_inventory_card",
                sourceAction: "listing_click",
                eventName: "seo_listing_click",
                listingId: listing.id,
                seoPageType: config.pageType,
                seoPageSlug: config.slug,
                pagePath: config.path,
              });
              trackSeoPageEvent(SEO_ANALYTICS_EVENTS.INVENTORY_CARD_CLICKED, {
                listingId: listing.id,
                pagePath: config.path,
                ...analytics,
              });
            }}
          />
        ))}
      </div>
    </section>
  );
}
